
import React from "react";
import { formatDistanceToNow } from "date-fns";
import { el } from "date-fns/locale";
import { Card, CardContent } from "@/components/ui/card";
import { Lead } from "@/context/LeadContext";
import LeadStatusBadge from "../ui-custom/LeadStatusBadge";
import ServiceBadge from "../ui-custom/ServiceBadge";
import { Button } from "@/components/ui/button";
import { MessageSquare, Eye, UserRound, Phone, MapPin, Clock, ExternalLink, Megaphone } from "lucide-react";
import { Link } from "react-router-dom";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";

interface LeadCardProps {
  lead: Lead;
  onSendSMS?: (leadId: string) => void;
  children?: React.ReactNode;
}

const LeadCard: React.FC<LeadCardProps> = ({ lead, onSendSMS, children }) => {
  const timeAgo = formatDistanceToNow(new Date(lead.createdAt), {
    addSuffix: true,
    locale: el
  });

  // Determine card accent color based on stage
  const getStageColor = () => {
    switch (lead.stage) {
      case 'Νέο': return 'border-l-blue-500';
      case 'σεΕκκρεμότητα': return 'border-l-amber-500';
      case 'έκλεισεΗΔουλειά': return 'border-l-emerald-500';
      case 'ήθελεΠληροφορίες': return 'border-l-slate-400';
      default: return 'border-l-slate-300';
    }
  };

  return (
    <Card className={cn(
      "group border-slate-200 hover:border-slate-300 hover:shadow-md transition-all duration-200",
      "border-l-4",
      getStageColor()
    )}>
      <CardContent className="p-4">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {/* Lead Avatar & Name */}
          <div className="flex items-center gap-3 min-w-0 flex-1">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center shrink-0">
              <span className="text-sm font-semibold text-slate-600">
                {lead.name.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-semibold text-slate-900 truncate">{lead.name}</h3>
                {lead.stage && <LeadStatusBadge status={lead.stage} />}
              </div>
              <div className="flex items-center gap-3 mt-1 text-sm text-slate-500 flex-wrap">
                <span className="flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  {timeAgo}
                </span>
                <ServiceBadge service={lead.service} />
                {lead.campaign && (
                  <span className="flex items-center gap-1 text-xs text-violet-600 bg-violet-50 border border-violet-200 rounded-full px-2 py-0.5 max-w-[180px] truncate" title={lead.campaign}>
                    <Megaphone className="h-3 w-3 shrink-0" />
                    {lead.campaign}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="flex flex-col gap-1.5 text-sm md:min-w-[200px]">
            <a
              href={`tel:${lead.phone}`}
              className="flex items-center gap-2 text-slate-600 hover:text-blue-600 transition-colors"
            >
              <Phone className="h-4 w-4 text-slate-400" />
              {lead.phone}
            </a>
            <div className="flex items-start gap-2 text-slate-500">
              <MapPin className="h-4 w-4 text-slate-400 shrink-0 mt-0.5" />
              <span className="line-clamp-1">{lead.address}</span>
            </div>
          </div>

          {/* Assigned Partner */}
          <div className="flex items-center gap-2 md:min-w-[150px]">
            <UserRound className="h-4 w-4 text-slate-400" />
            {lead.partnerName ? (
              <span className="text-sm text-slate-700 font-medium truncate">{lead.partnerName}</span>
            ) : (
              <span className="text-sm text-amber-600 italic">Χωρίς συνεργάτη</span>
            )}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2 shrink-0">
            {children ? (
              children
            ) : (
              <>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onSendSMS && onSendSMS(lead.id)}
                        disabled={!lead.partnerId}
                        className="h-9 w-9 p-0 text-slate-500 hover:text-blue-600 hover:bg-blue-50"
                        aria-label={`Αποστολή SMS στον ${lead.name}`}
                      >
                        <MessageSquare className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Αποστολή SMS</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link to={`/lead/${lead.id}`}>
                        <Button
                          size="sm"
                          className="h-9 gap-2 bg-slate-900 hover:bg-slate-800"
                          aria-label={`Προβολή λεπτομερειών για ${lead.name}`}
                        >
                          <Eye className="h-4 w-4" />
                          <span className="hidden sm:inline">Προβολή</span>
                        </Button>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Λεπτομέρειες επαφής</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LeadCard;
